import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { trackEvent } from "../../shared/services/playerEvents";
import { isPhoneGateSuppressed } from "../../shared/utils/phoneGateSuppression";
import { Button } from "@/components/ui/button";

/**
 * PhoneGate: CONTEXTUAL phone-verification sheet (Didit), opened on demand.
 *
 * This used to be an impassable wall on site entry for every player — and it
 * measurably killed deposits (the collapse started the day it shipped,
 * 2026-08-05: new players were walled mid-first-session, and the Didit round
 * trip corrupted fragile fresh sessions). The one-phone-per-player check now
 * gates VALUE EXTRACTION instead: the backend returns 403 PHONE_REQUIRED from
 * free-Cluck-Bucks claims (CB wheels, CB calendar/VIP rewards, AMOE codes) and
 * redemption initiation, the apiClient interceptor announces it via the
 * `cluck:phone-required` window event, and this sheet opens with "verify to
 * claim" framing. Entry, play, and purchase are never interrupted.
 *
 * Dismissable by design ("Not now") — declining just means the claim stays
 * unavailable; the server enforces the actual rule. Tapping "Verify my phone"
 * runs the Didit hosted flow; the return lands on /kyc/callback, which polls
 * until the webhook flips phone_verified, then sends the player back to any
 * in-flight checkout (checkoutIntent) or the lobby.
 */
const PhoneGate = () => {
  const { needsPhone, startPhoneVerification } = useAuth();

  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Open on the interceptor's announcement of a PHONE_REQUIRED response.
  useEffect(() => {
    const onRequired = () => {
      // A surface that shows its own verify CTA suppresses this sheet. Radix
      // Dialogs trap pointer events inside their own subtree, so opening over
      // one paints a button the player cannot click without first dismissing
      // the thing they were trying to act on.
      if (isPhoneGateSuppressed()) return;
      setError("");
      setOpen(true);
      trackEvent("phone_gate_shown");
    };
    window.addEventListener("cluck:phone-required", onRequired);
    return () => window.removeEventListener("cluck:phone-required", onRequired);
  }, []);

  // The gate clears itself if verification completes while it is open
  // (needsPhone flips false once /verify reflects the webhook).
  //
  // It must close on the TRANSITION, not on the value. needsPhone is set by the
  // gate check at sign-in, so a session that predates that check reports false
  // while the server still refuses phone-gated actions. Closing on the bare
  // value meant the sheet opened on a PHONE_REQUIRED 403 and was shut again in
  // the same tick, so the player got a refusal with no way to act on it and
  // nothing on screen to explain it. That hit every phone-gated surface, not
  // just the wheel.
  const wasNeedingPhone = useRef(needsPhone);
  useEffect(() => {
    if (open && wasNeedingPhone.current && !needsPhone) setOpen(false);
    wasNeedingPhone.current = needsPhone;
  }, [open, needsPhone]);

  if (!open) return null;

  const handleVerify = async () => {
    if (submitting) return;
    setError("");
    setSubmitting(true);
    const result = await startPhoneVerification();
    if (result.ok && result.url) {
      trackEvent("phone_gate_started");
      // Redirect to Didit's hosted verification. The player returns via
      // DIDIT_CALLBACK_URL (/kyc/callback), which polls until verified.
      window.location.assign(result.url);
      return;
    }
    // Already verified at Didit; the gate closes on the needsPhone transition.
    if (result.ok && result.alreadyVerified) {
      trackEvent("phone_gate_completed");
      setSubmitting(false);
      setOpen(false);
      return;
    }
    setSubmitting(false);
    setError(
      result.message || "Something went wrong. Please try again in a moment.",
    );
  };

  const handleNotNow = () => {
    trackEvent("phone_gate_abandoned");
    setOpen(false);
  };

  return (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="phone-gate-title"
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-card-foreground shadow-lg sm:p-8">
        <div className="space-y-6">
          <div className="space-y-2 text-center sm:text-left">
            <h2 id="phone-gate-title" className="text-xl font-semibold">
              Verify your phone to claim
            </h2>
            <p className="text-sm text-muted-foreground">
              Cluck Bucks rewards are one per player, so we verify one phone
              number per player before awarding them. It only takes a moment,
              and you only ever do it once.
            </p>
          </div>

          {error ? (
            <p className="text-sm text-red-500" role="alert">
              {error}
            </p>
          ) : null}

          <div className="space-y-3">
            <Button
              type="button"
              variant="gold"
              size="lg"
              className="w-full"
              onClick={handleVerify}
              disabled={submitting}
            >
              {submitting ? "Opening…" : "Verify my phone"}
            </Button>

            <button
              type="button"
              onClick={handleNotNow}
              className="w-full text-center text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneGate;
